import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import RecommendPeopleRail from "../components/RecommendPeopleRail";
import useSeo from "../hooks/useSeo";
import { resolveEmbedSrc, AUDIENCE_LABELS, ACCESS_LEVEL_LABELS } from "../utils/videoEmbed";
import { apiFetch, assetUrl } from "../utils/api";

export default function CoursesPage() {
  const [searchParams] = useSearchParams();
  const initialCategory = searchParams.get("category") || "";
  const focusCourseId = Number(searchParams.get("course") || 0);
  const currentUserId = localStorage.getItem("user_id");
  const [categories, setCategories] = useState({});
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState(initialCategory);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useSeo({
    title: "AI婚恋学院_恋爱婚姻成长课程 - AI婚恋平台",
    metaDescription:
      "AI婚恋学院：认识自己、恋爱能力、婚姻能力、个人成长。视频托管第三方平台，本站外链播放。",
    ogDescription: "婚恋与关系成长视频课程。",
    keywords: "婚恋学院,恋爱课程,婚姻课程,成长课程,沟通课程",
    path: "/courses",
  });

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (category) params.set("category", category);
    if (currentUserId) params.set("current_user_id", currentUserId);
    apiFetch(`/api/public/video-courses?${params.toString()}`)
      .then((res) => res.json())
      .then((data) => {
        const list = data.items || [];
        setCategories(data.categories || {});
        setItems(list);
        if (focusCourseId) {
          const hit = list.find((x) => Number(x.id) === focusCourseId);
          if (hit) setSelected(hit);
        }
      })
      .catch((err) => {
        console.error("加载视频课程失败:", err);
        setCategories({});
        setItems([]);
      })
      .finally(() => setLoading(false));
  }, [category, currentUserId, focusCourseId]);

  const embedSrc = selected && selected.can_play ? resolveEmbedSrc(selected) : "";

  const accessHint = (course) => {
    const level = course.access_level || "public";
    if (level === "public" || course.can_play) return null;
    if (level === "member") {
      return { text: "登录后可观看", link: "/login", linkText: "去登录" };
    }
    if (level === "paid") {
      if (!currentUserId) {
        return { text: "付费会员专享，请先登录", link: "/login", linkText: "去登录" };
      }
      return { text: "本课程为付费会员专享", link: "/center", linkText: "了解会员" };
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-pink-50 to-purple-50 flex flex-col">
      <Header />

      <main className="flex-1 px-4 py-10">
        <div className="max-w-6xl mx-auto flex flex-col lg:flex-row gap-6 items-start">
          <div className="flex-1 min-w-0 space-y-6">
          <div className="lg:hidden">
            <RecommendPeopleRail layout="strip" />
          </div>
          <header className="text-center space-y-2">
            <h1 className="text-3xl font-bold text-gray-800">AI 婚恋学院</h1>
            <p className="text-gray-600 text-sm max-w-xl mx-auto">
              认识自己 · 恋爱能力 · 婚姻能力 · 个人成长。视频托管第三方平台，本站外链播放。
            </p>
            <Link to="/ai/growth" className="inline-block text-sm text-rose-600 underline">
              先看我的成长报告，再选课 →
            </Link>
          </header>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { key: "self", title: "认识自己", desc: "性格 / 情感模式 / 原生家庭", cat: "growth" },
              { key: "love_skill", title: "恋爱能力", desc: "吸引力 / 沟通 / 矛盾", cat: "dating" },
              { key: "marriage_skill", title: "婚姻能力", desc: "责任 / 财务 / 家庭", cat: "marriage_prep" },
              { key: "personal_growth", title: "个人成长", desc: "情绪 / 职业 / 自我提升", cat: "growth" },
            ].map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setCategory(t.cat)}
                className={`text-left rounded-2xl border p-4 transition ${
                  category === t.cat
                    ? "border-rose-400 bg-rose-50"
                    : "border-gray-100 bg-white hover:border-rose-200"
                }`}
              >
                <div className="font-semibold text-gray-900">{t.title}</div>
                <div className="text-xs text-gray-500 mt-1">{t.desc}</div>
              </button>
            ))}
          </div>

          <div className="flex flex-wrap justify-center gap-2">
            <button
              type="button"
              onClick={() => setCategory("")}
              className={`px-4 py-1.5 rounded-full text-sm ${
                !category ? "bg-pink-500 text-white" : "bg-white text-gray-600 border"
              }`}
            >
              全部
            </button>
            {Object.entries(categories).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setCategory(key)}
                className={`px-4 py-1.5 rounded-full text-sm ${
                  category === key ? "bg-pink-500 text-white" : "bg-white text-gray-600 border"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {loading ? (
            <p className="text-center text-gray-400 py-16">加载中...</p>
          ) : items.length === 0 ? (
            <p className="text-center text-gray-400 py-16">暂无课程，请稍后再来</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((course) => {
                const locked = !course.can_play;
                return (
                  <button
                    key={course.id}
                    type="button"
                    onClick={() => setSelected(course)}
                    className="text-left bg-white rounded-xl shadow hover:shadow-md border border-transparent hover:border-pink-200 transition overflow-hidden relative"
                  >
                    {course.cover_url ? (
                      <img src={assetUrl(course.cover_url)} alt="" className="w-full h-36 object-cover" />
                    ) : (
                      <div className="w-full h-36 bg-gradient-to-br from-indigo-100 to-pink-100 flex items-center justify-center text-4xl">
                        🎬
                      </div>
                    )}
                    {locked && (
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <span className="bg-white/90 text-gray-700 text-xs px-3 py-1 rounded-full">
                          {ACCESS_LEVEL_LABELS[course.access_level] || "需权限"}
                        </span>
                      </div>
                    )}
                    <div className="p-4 space-y-1">
                      {course.is_featured ? (
                        <span className="text-xs text-pink-500 font-medium">推荐</span>
                      ) : null}
                      <h2 className="font-bold text-gray-800 line-clamp-2">{course.title}</h2>
                      <p className="text-xs text-gray-500">
                        {course.category_label} · {AUDIENCE_LABELS[course.audience] || "全部人群"}
                        {(course.access_level && course.access_level !== "public") && (
                          <> · {ACCESS_LEVEL_LABELS[course.access_level]}</>
                        )}
                      </p>
                      {course.duration_label && (
                        <p className="text-xs text-gray-400">{course.duration_label}</p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          </div>
          <div className="hidden lg:block shrink-0">
            <RecommendPeopleRail layout="sidebar" />
          </div>
        </div>
      </main>

      {selected && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setSelected(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 space-y-4">
              <div className="flex justify-between items-start gap-4">
                <div>
                  <p className="text-xs text-pink-500">{selected.category_label}</p>
                  <h2 className="text-xl font-bold text-gray-800">{selected.title}</h2>
                  <p className="text-xs text-gray-500 mt-1">
                    {selected.platform_label} · {AUDIENCE_LABELS[selected.audience]}
                    {selected.access_level && selected.access_level !== "public" && (
                      <> · {ACCESS_LEVEL_LABELS[selected.access_level]}</>
                    )}
                    {selected.duration_label ? ` · ${selected.duration_label}` : ""}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelected(null)}
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                >
                  ×
                </button>
              </div>

              {!selected.can_play ? (
                <div className="rounded-xl bg-indigo-50 border border-indigo-100 p-8 text-center space-y-3">
                  <p className="text-4xl">🔒</p>
                  <p className="text-gray-700 font-medium">
                    {ACCESS_LEVEL_LABELS[selected.access_level] || "本课程"}专享内容
                  </p>
                  <p className="text-sm text-gray-500">{accessHint(selected)?.text}</p>
                  {accessHint(selected) && (
                    <Link
                      to={accessHint(selected).link}
                      className="inline-block px-5 py-2.5 bg-pink-500 text-white rounded-full text-sm"
                    >
                      {accessHint(selected).linkText}
                    </Link>
                  )}
                </div>
              ) : embedSrc ? (
                <div className="aspect-video rounded-xl overflow-hidden bg-black">
                  <iframe
                    src={embedSrc}
                    title={selected.title}
                    className="w-full h-full"
                    allowFullScreen
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    frameBorder="0"
                    referrerPolicy="no-referrer-when-downgrade"
                    sandbox="allow-scripts allow-same-origin allow-popups allow-presentation"
                  />
                </div>
              ) : (
                <div className="rounded-xl bg-amber-50 border border-amber-100 p-6 text-center text-sm text-gray-600 space-y-2">
                  <p>当前链接暂无法在本站嵌入播放。</p>
                  <p className="text-xs text-gray-500 text-left leading-relaxed">
                    • 普通 B 站视频：链接里要有 <strong>BV</strong> 编号（如 /video/BV1xx…）<br />
                    • B 站晚会/番剧：链接里常有 <strong>/ep</strong> 编号（如 /bangumi/play/ep3915324），现已支持<br />
                    • 腾讯/优酷：需在后台填写「站内播放地址」（嵌入代码里的 src）<br />
                    • 或点击下方按钮前往原平台观看
                  </p>
                </div>
              )}

              {selected.description && (
                <p className="text-sm text-gray-600 whitespace-pre-line">{selected.description}</p>
              )}

              {selected.can_play && selected.video_url && (
                <a
                  href={selected.video_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-5 py-2.5 bg-pink-500 text-white rounded-full text-sm"
                >
                  {embedSrc ? `也可前往${selected.platform_label}观看` : `前往${selected.platform_label}观看`}
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
